import {
  Box,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  DataTable,
  DropdownButton,
  LoadingOverlay,
  ModuleTitle,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { withCan } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import ExportBtn from "./ExportBtn";
import { Store, StoreContext } from "./Store";
import { Table } from "./Table";

export const Report = withCan(
  {
    do: [
      "timeTracking_report_read_all",
      "timeTracking_report_read_team",
      "timeTracking_report_read_own",
    ],
  },
  observer(function Report() {
    const appStore = useStore();
    const [store] = useState(() => new Store(appStore));

    return (
      <StoreContext.Provider value={store}>
        <HStack justify="space-between" mb="5">
          <ModuleTitle>
            <Trans>Time Report</Trans>
          </ModuleTitle>

          <HStack spacing="8">
            <VStack align="end" spacing="0">
              <p className="uppercase font-semibold text-xl text-inkLighter">
                <Trans>total</Trans>
                &nbsp;
                {appStore.workspaceStore.settings?.timeTrackingRateTypeReadable()}
              </p>

              <p className="font-semibold">
                {appStore.workspaceStore.settings?.convertTimeTrackingRate(
                  store.summary?.tracked_time ?? 0,
                )}
              </p>
            </VStack>
          </HStack>
        </HStack>

        <DataTable>
          <DataTable.NavBar>
            <Box py="4">
              <FiltersPanelRoot filters={store.where}>
                <Stack flexDir="column" spacing="4">
                  <HStack justifyContent="space-between">
                    <HStack spacing="2">
                      <PeriodPicker
                        value={store.dateRangeValue}
                        onChange={(range) => {
                          store.dateRangeFilter?.setValue([
                            toApiDate(range.start),
                            toApiDate(range.end),
                          ]);
                        }}
                      />

                      <Menu>
                        <MenuButton
                          as={DropdownButton}
                          sx={
                            store.groupBy
                              ? {
                                  bg: "riptide.50",
                                  borderColor: "riptide.400",
                                  _hover: {
                                    bg: "riptide.100",
                                  },
                                  _active: {
                                    bg: "riptide.100",
                                  },
                                }
                              : undefined
                          }
                        >
                          <Trans>Group By:</Trans>&nbsp;
                          {store.activeGroupByLabel}
                        </MenuButton>

                        <Portal>
                          <MenuList>
                            {store.groupByOptions.map(({ value, label }) => (
                              <MenuItem
                                key={value}
                                onClick={() => {
                                  store.setGroupBy(value);
                                }}
                              >
                                {label}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Portal>
                      </Menu>

                      <FiltersPanelFilters
                        innerMenuPlacement="right-start"
                        clearButtonSide="right"
                      />
                    </HStack>

                    <HStack spacing="2">
                      <ExportBtn where={store.where} />
                    </HStack>
                  </HStack>
                </Stack>
              </FiltersPanelRoot>
            </Box>
          </DataTable.NavBar>

          <LoadingOverlay
            isLoading={store.isReportLoading || store.areFiltersLoading}
            size="md"
            position="absolute"
          >
            <Table />
          </LoadingOverlay>

          <Pagination store={store.pagination} />
        </DataTable>
      </StoreContext.Provider>
    );
  }),
);
